<script setup lang="ts">
    import { Button } from '@/components/ui/button';
    import {
        Dialog,
        DialogContent,
        DialogHeader,
        DialogTitle,
        DialogClose,
        DialogFooter,
        DialogTrigger,
        DialogBody,
    } from '@/components/ui/dialog';
    import { ChevronRight } from 'lucide-vue-next';
    import { computed, ref } from 'vue';
    import { DatePicker } from '@/components/ui/form';
    import { getLocalTimeZone, today, endOfMonth, startOfMonth } from '@internationalized/date';
    import { Label } from '@/components/ui/label';
    import { toast } from 'vue-sonner';
    import { useI18n } from 'vue-i18n';
    import { useMutation } from '@tanstack/vue-query';
    import { usePage } from '@inertiajs/vue3';
    import axios from 'axios';
    import type { SharedData } from '@/types';

    const { t } = useI18n();

    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);

    const open = ref(false);

    const handleClose = () => {
        open.value = false;
    };

    const currentDate = today(getLocalTimeZone());
    const startDate = ref(startOfMonth(currentDate));
    const endDate = ref(endOfMonth(currentDate));

    const { mutate, isPending } = useMutation({
        mutationFn: (formData: { from: string; to: string }) =>
            axios.get(route('v2.salary.employees.changes.export', selectedCompanyId.value), {
                params: formData,
            }),
        onSuccess: (response) => {
            if (response.data.no_changes !== undefined) {
                toast.success(response.data.no_changes);
            } else {
                document.location.href = response.request.responseURL;
            }
            open.value = false;
        },
        onError: (error: any) => {
            toast.error(t('forms.system_error'));
        },
    });

    function exportChanges() {
        mutate({
            from: startDate.value.toString(),
            to: endDate.value.toString(),
        });
    }
</script>

<template>
    <Dialog v-model:open="open">
        <DialogTrigger as-child>
            <Button
                variant="ghost_primary"
                size="xs"
                width="narrow"
            >
                {{ $t('employee.changes.export') }}
                <ChevronRight class="text-primary" />
            </Button>
        </DialogTrigger>
        <DialogContent class="sm:max-w-lg">
            <DialogHeader>
                <DialogTitle>
                    {{ t('forms.export_all_changes_title') }}
                </DialogTitle>
            </DialogHeader>

            <DialogBody>
                <div class="grid grid-cols-2 gap-2">
                    <div class="flex flex-col gap-2">
                        <Label for="startDate">{{ t('forms.from') }}</Label>
                        <DatePicker v-model="startDate" />
                    </div>
                    <div class="flex flex-col gap-2">
                        <Label for="endDate">{{ t('forms.to') }}</Label>
                        <DatePicker v-model="endDate" />
                    </div>
                </div>
            </DialogBody>

            <DialogFooter class="gap-2 border-t sm:justify-center">
                <DialogClose as-child>
                    <Button
                        variant="outline"
                        size="sm"
                        @click="handleClose"
                    >
                        {{ t('forms.close') }}
                    </Button>
                </DialogClose>
                <Button
                    :disabled="isPending"
                    :loading="isPending"
                    size="sm"
                    @click="exportChanges"
                >
                    {{ $t('employee.changes.export') }}
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
